import { useEffect, useMemo, useRef, useState } from 'react';
import { useInboxStore, type Attachment, type Message } from '../../stores/inbox-store';
import { supabase } from '../../lib/supabase';
import { AanFieldPicker } from './AanFieldPicker';

import { MAX_MAIL_BIJLAGEN_BYTES, MAX_MAIL_BIJLAGEN_LABEL } from '../../lib/factuurconcept';

const MAX_TOTAL_ATTACHMENT_BYTES = MAX_MAIL_BIJLAGEN_BYTES;

interface Props {
  message: Message;
  originalAttachments: Attachment[];
  onClose: () => void;
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ForwardDialog({ message, originalAttachments, onClose }: Props) {
  const activeThread = useInboxStore((s) => s.activeThread);
  const conversation = activeThread?.conversation;

  const defaultSubject = useMemo(() => {
    const base = (message.subject || conversation?.subject || '').replace(/^(re:|fwd?:)\s*/i, '');
    return `Fwd: ${base}`.trim();
  }, [message.subject, conversation?.subject]);

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState(defaultSubject);
  const [bodyText, setBodyText] = useState('');
  const [includeOriginals, setIncludeOriginals] = useState(originalAttachments.length > 0);
  const [extraFiles, setExtraFiles] = useState<File[]>([]);
  const [extraFilesError, setExtraFilesError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSending) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isSending, onClose]);

  // Autofocus To-field. Dat is het eerste tekstveld in de dialoog; het veld
  // zelf komt uit AanFieldPicker en draagt daarom geen eigen marker-attribuut.
  useEffect(() => {
    const el = dialogRef.current?.querySelector<HTMLInputElement>('input[type="text"]');
    el?.focus();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const next = [...extraFiles, ...picked];
    const total = next.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL_ATTACHMENT_BYTES) {
      setExtraFilesError(`Bestanden samen ${(total / 1024 / 1024).toFixed(1)} MB, maximaal ${MAX_MAIL_BIJLAGEN_LABEL}. Verklein de foto of stuur hem los.`);
    } else {
      setExtraFilesError(null);
    }
    setExtraFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setExtraFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      const total = next.reduce((s, f) => s + f.size, 0);
      if (total <= MAX_TOTAL_ATTACHMENT_BYTES) setExtraFilesError(null);
      return next;
    });
  };

  const handleSend = async () => {
    if (!to.trim()) {
      setError('Vul een ontvanger in.');
      return;
    }
    if (extraFilesError) {
      setError(extraFilesError);
      return;
    }
    if (!conversation) {
      setError('Geen actief gesprek.');
      return;
    }

    setIsSending(true);
    setError(null);
    setWarning(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Niet ingelogd');

      const extraPayload = extraFiles.length
        ? await Promise.all(
            extraFiles.map(async (f) => ({
              filename: f.name,
              mimeType: f.type || 'application/octet-stream',
              dataBase64: await fileToBase64(f),
            })),
          )
        : undefined;

      const html = bodyText.replace(/\n/g, '<br/>');

      const res = await fetch('/api/inbox/forward', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversation.id,
          message_id: message.id,
          to: to.trim(),
          cc: cc.trim() || undefined,
          bcc: bcc.trim() || undefined,
          subject: subject.trim() || undefined,
          body_html: html,
          body_text: bodyText,
          include_original_attachments: includeOriginals,
          attachments: extraPayload,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Doorsturen mislukt' }));
        throw new Error(err.error || 'Doorsturen mislukt');
      }

      const data = await res.json();
      if (data.warning) {
        setWarning(data.warning);
        // Trigger thread refresh so the forward shows up
        void useInboxStore.getState().setActiveThread(conversation.id);
        setTimeout(onClose, 1500);
        return;
      }

      // Refresh thread so the forwarded message appears in the timeline
      void useInboxStore.getState().setActiveThread(conversation.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Doorsturen mislukt');
    } finally {
      setIsSending(false);
    }
  };

  const totalExtraBytes = extraFiles.reduce((s, f) => s + f.size, 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={() => !isSending && onClose()}
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Bericht doorsturen</h3>
          <button
            onClick={onClose}
            disabled={isSending}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
            title="Sluiten (Esc)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Aan</label>
            {/* Type-ahead over reparateurs, partners, klanten en eerdere
                correspondenten — doorsturen naar een reparateur hoeft niet
                langer via het adres opzoeken in een ander scherm. */}
            <AanFieldPicker
              variant="stacked"
              value={to}
              onChange={setTo}
              showCcBccButton={!showCcBcc}
              onShowCcBcc={() => setShowCcBcc(true)}
            />
          </div>

          {showCcBcc && (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Cc</label>
                <input
                  type="text"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Bcc</label>
                <input
                  type="text"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Onderwerp</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Bericht <span className="text-slate-400">(optioneel — komt boven de doorgestuurde mail)</span>
            </label>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder="Hoi, zie hieronder…"
              rows={4}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[100px]"
            />
          </div>

          {originalAttachments.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeOriginals}
                  onChange={(e) => setIncludeOriginals(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-sm text-slate-700">
                  Originele bijlagen meesturen
                  <span className="block text-xs text-slate-500 mt-0.5">
                    Grote foto's worden zo nodig verkleind voor verzending. De originelen blijven behouden.
                  </span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    {originalAttachments.length} bestand{originalAttachments.length === 1 ? '' : 'en'}:{' '}
                    {originalAttachments.map((a) => a.filename).join(', ')}
                  </span>
                </span>
              </label>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-500">Extra bijlagen</span>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs text-indigo-600 hover:text-indigo-700"
              >
                + Bestand toevoegen
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
            {extraFiles.length > 0 && (
              <div className="space-y-1.5">
                {extraFiles.map((f, idx) => (
                  <div
                    key={`${f.name}-${idx}`}
                    className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm"
                  >
                    <span className="flex-1 truncate text-slate-700">{f.name}</span>
                    <span className="text-xs text-slate-400">{formatFileSize(f.size)}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-slate-400 hover:text-rose-500"
                      title="Verwijderen"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <p className="text-xs text-slate-400">
                  Totaal {(totalExtraBytes / 1024 / 1024).toFixed(1)} MB van {MAX_MAIL_BIJLAGEN_LABEL}
                </p>
              </div>
            )}
            {extraFilesError && (
              <p className="text-xs text-rose-600 mt-1">{extraFilesError}</p>
            )}
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
          {warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
              {warning}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isSending}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            onClick={handleSend}
            disabled={isSending || !to.trim() || !!extraFilesError}
            className="px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {isSending && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            Doorsturen
          </button>
        </div>
      </div>
    </div>
  );
}
